import { useState } from "react";
import { Link2, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

export const shareLink = (bookId, token) => `${window.location.origin}/?book=${bookId}&share=${token}`;

// Owner-only: books are private by default; a link exists only after the owner explicitly creates it.
export const ShareControls = ({ book, onChange }) => {
  const [busy, setBusy] = useState(false);
  if (book.shared_view) return null;
  const create = async () => {
    setBusy(true);
    try {
      const { share_token } = await api.createShare(book.id);
      onChange?.({ ...book, share_token });
      await navigator.clipboard?.writeText(shareLink(book.id, share_token)).catch(() => {});
      toast.success("Share link copied. Anyone with this link can read the book.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const revoke = async () => {
    setBusy(true);
    try {
      await api.revokeShare(book.id);
      onChange?.({ ...book, share_token: null });
      toast.success("Sharing stopped. The old link no longer works.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return book.share_token ? (
    <div className="flex items-center gap-2">
      <input readOnly data-testid="share-link-input" value={shareLink(book.id, book.share_token)} onFocus={(e) => e.target.select()}
        className="w-72 bg-navy-950/60 border border-gold-500/20 rounded-lg px-3 py-2 font-ui text-xs text-slate-200" />
      <button data-testid="share-revoke-button" onClick={revoke} disabled={busy}
        className="inline-flex items-center gap-1.5 font-ui text-sm text-slate-200 border border-white/20 hover:border-red-400/60 rounded-xl px-4 py-2.5 transition-colors">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />} Stop sharing
      </button>
    </div>
  ) : (
    <button data-testid="share-create-button" onClick={create} disabled={busy}
      className="inline-flex items-center gap-2 font-ui text-sm text-slate-200 border border-white/20 hover:border-gold-400/60 rounded-xl px-5 py-3.5 transition-colors">
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Create share link
    </button>
  );
};
